import React, { useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { FileText, Upload, Trash2 } from 'lucide-react';

const FORESTA_ID = 'edif_6034e848d0ed';
const VALID_TABS = ['general', 'superficies', 'capacidad', 'inversion', 'documentacion', 'servicios'];
const TAB_LS_KEY = 'rentagc.unidadDialog.lastTab';

export const UnidadDialog = ({
  edificioId,
  handleSaveUnidad,
  isUnidadDialogOpen,
  saving,
  selectedUnidad,
  setIsUnidadDialogOpen,
  setUnidadForm,
  setUnidadTab,
  unidadForm,
  unidadTab,
  // Documentación: handlers para abrir los modales existentes de carga
  onOpenCartaOferta,
  onOpenPosesion,
  onOpenEscritura,
  // Escritura general del edificio (solo Torre Emilia, lectura)
  escrituraGeneralEdificio,
  onDownloadEscrituraGeneral,
  // Eliminar unidad
  onDeleteUnidad,
}) => {
  const isForesta = edificioId === FORESTA_ID;
  const isTorreEmilia = edificioId === 'edif_74b24adaccb6';

  // Persistir/restaurar último tab abierto
  useEffect(() => {
    if (isUnidadDialogOpen) {
      try {
        const stored = window.localStorage.getItem(TAB_LS_KEY);
        if (stored && VALID_TABS.includes(stored)) {
          // Si el tab guardado es "documentacion" pero la unidad no existe, fallback a general
          if (stored === 'documentacion' && !selectedUnidad) setUnidadTab('general');
          else setUnidadTab(stored);
        } else {
          setUnidadTab('general');
        }
      } catch (_e) { setUnidadTab('general'); }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUnidadDialogOpen]);

  const handleTabChange = (v) => {
    setUnidadTab(v);
    try { window.localStorage.setItem(TAB_LS_KEY, v); } catch (_e) { /* noop */ }
  };

  const updateAmoblamientoItem = (key, value) => {
    setUnidadForm({
      ...unidadForm,
      amoblamiento_detalle: {
        ...(unidadForm.amoblamiento_detalle || {}),
        [key]: value,
      },
    });
  };

  const monto = unidadForm.monto_inversion || {};
  const setMontoField = (field, value) => {
    setUnidadForm({
      ...unidadForm,
      monto_inversion: { ...monto, [field]: value },
    });
  };

  const tieneCartaOferta = !!(selectedUnidad?.carta_oferta_archivo?.filename || selectedUnidad?.carta_oferta_archivo?.data);
  const tienePosesion = !!(selectedUnidad?.posesion_archivo?.filename || selectedUnidad?.posesion_archivo?.data);
  const tieneEscritura = !!selectedUnidad?.escritura_archivo;

  return (
    <Dialog open={isUnidadDialogOpen} onOpenChange={setIsUnidadDialogOpen}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="unidad-dialog">
        <DialogHeader>
          <DialogTitle>{selectedUnidad ? `Datos unidad — ${selectedUnidad.nombre_unidad}` : 'Nueva Unidad'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSaveUnidad} className="space-y-4">
          <Tabs value={unidadTab} onValueChange={handleTabChange} className="w-full">
            <TabsList className="grid w-full grid-cols-6">
              <TabsTrigger value="general" data-testid="tab-general">General</TabsTrigger>
              <TabsTrigger value="superficies" data-testid="tab-superficies">Superficies</TabsTrigger>
              <TabsTrigger value="capacidad" data-testid="tab-capacidad">Capacidad</TabsTrigger>
              <TabsTrigger value="inversion" data-testid="tab-inversion">Inversión</TabsTrigger>
              <TabsTrigger value="documentacion" data-testid="tab-documentacion" disabled={!selectedUnidad}>Documentación</TabsTrigger>
              <TabsTrigger value="servicios" data-testid="tab-servicios">Servicios</TabsTrigger>
            </TabsList>

            {/* GENERAL */}
            <TabsContent value="general" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Nombre Unidad *</Label>
                  <Input value={unidadForm.nombre_unidad} onChange={(e) => setUnidadForm({ ...unidadForm, nombre_unidad: e.target.value })} placeholder="Ej: Oficina 3A" required />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Tipo *</Label>
                  <Select value={unidadForm.tipo} onValueChange={(v) => setUnidadForm({ ...unidadForm, tipo: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="oficina">Oficina</SelectItem>
                      <SelectItem value="local">Local</SelectItem>
                      <SelectItem value="cowork">Cowork</SelectItem>
                      <SelectItem value="cochera">Cochera</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Nomenclatura Catastral</Label>
                <Input value={unidadForm.nomenclatura_catastral || ''} onChange={(e) => setUnidadForm({ ...unidadForm, nomenclatura_catastral: e.target.value })} placeholder="Ej: 05-01-10-0001-000001" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Padrón Territorial</Label>
                <Input
                  value={unidadForm.padron_territorial || ''}
                  onChange={(e) => setUnidadForm({ ...unidadForm, padron_territorial: e.target.value })}
                  placeholder="Ej: 12345/6"
                  data-testid="unidad-padron-territorial"
                />
              </div>

              {/* Cochera (Foresta) */}
              {isForesta && (
                <div className="rounded-md border border-slate-200 p-3 space-y-3">
                  <div className="text-sm font-semibold text-slate-700">Cochera</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">¿Tiene cochera?</Label>
                      <Select
                        value={unidadForm.tiene_cochera ? 'si' : 'no'}
                        onValueChange={(v) => setUnidadForm({ ...unidadForm, tiene_cochera: v === 'si' })}
                      >
                        <SelectTrigger data-testid="select-tiene-cochera"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="no">No</SelectItem>
                          <SelectItem value="si">Sí</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Nº de cochera</Label>
                      <Input
                        data-testid="input-cochera-numero"
                        value={unidadForm.cochera_numero || ''}
                        onChange={(e) => setUnidadForm({ ...unidadForm, cochera_numero: e.target.value })}
                        placeholder="Ej: 12"
                        disabled={!unidadForm.tiene_cochera}
                        className={!unidadForm.tiene_cochera ? 'bg-slate-100' : ''}
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Observaciones</Label>
                    <textarea
                      data-testid="input-cochera-observaciones"
                      value={unidadForm.cochera_observaciones || ''}
                      onChange={(e) => setUnidadForm({ ...unidadForm, cochera_observaciones: e.target.value })}
                      placeholder="Detalles"
                      disabled={!unidadForm.tiene_cochera}
                      rows={2}
                      className={`w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 ${!unidadForm.tiene_cochera ? 'bg-slate-100' : ''}`}
                    />
                  </div>
                </div>
              )}

              {/* Amoblamiento (Foresta) */}
              {isForesta && (
                <div className="rounded-md border border-slate-200 p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-slate-700">Amoblamiento</div>
                    <Select
                      value={unidadForm.tiene_amoblamiento ? 'si' : 'no'}
                      onValueChange={(v) => setUnidadForm({ ...unidadForm, tiene_amoblamiento: v === 'si' })}
                    >
                      <SelectTrigger data-testid="select-tiene-amoblamiento" className="w-24 h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="no">No</SelectItem>
                        <SelectItem value="si">Sí</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className={`grid grid-cols-3 gap-2 ${!unidadForm.tiene_amoblamiento ? 'opacity-50 pointer-events-none' : ''}`}>
                    {[
                      { key: 'aires', label: 'Aires' },
                      { key: 'cortinas', label: 'Cortinas' },
                      { key: 'muebles', label: 'Muebles' },
                      { key: 'sillas', label: 'Sillas' },
                      { key: 'pizarra', label: 'Pizarra' },
                    ].map(item => (
                      <label key={item.key} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                        <input
                          type="checkbox"
                          data-testid={`check-amoblamiento-${item.key}`}
                          checked={!!(unidadForm.amoblamiento_detalle && unidadForm.amoblamiento_detalle[item.key])}
                          onChange={(e) => updateAmoblamientoItem(item.key, e.target.checked)}
                          disabled={!unidadForm.tiene_amoblamiento}
                          className="rounded border-slate-300"
                        />
                        {item.label}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>

            {/* SUPERFICIES */}
            <TabsContent value="superficies" className="space-y-4 mt-4">
              <div className="rounded-md border border-slate-200 p-3 space-y-3">
                <div className="text-sm font-semibold text-slate-700">Superficies</div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                  {[
                    { k: 'm2_cubiertos',        l: 'm² Cubiertos propios',     tid: 'input-m2-cubiertos' },
                    { k: 'm2_semicubiertos',    l: 'm² Semicubiertos propios', tid: 'input-m2-semicubiertos' },
                    { k: 'm2_descubiertos',     l: 'm² Descubiertos propios',  tid: 'input-m2-descubiertos' },
                    { k: 'm2_cubiertos_total',  l: 'm² Cubiertos',             tid: 'input-m2-cubiertos-total' },
                    { k: 'm2_totales',          l: 'm² Totales',               tid: 'input-m2-totales' },
                    { k: 'm2_cochera',          l: 'm² Cochera',               tid: 'input-m2-cochera' },
                    { k: 'm2_expensas',         l: '% Expensas',               tid: 'input-m2-expensas' },
                  ].map(it => (
                    <div key={it.k} className="space-y-1">
                      <Label className="text-xs text-slate-600">{it.l}</Label>
                      <Input
                        data-testid={it.tid}
                        type="number"
                        step="0.01"
                        value={unidadForm[it.k] ?? ''}
                        onChange={(e) => setUnidadForm({ ...unidadForm, [it.k]: e.target.value })}
                        placeholder="0"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* CAPACIDAD */}
            <TabsContent value="capacidad" className="space-y-4 mt-4" data-testid="tab-capacidad-content">
              <div className="rounded-md border border-slate-200 p-3 space-y-3">
                <div className="text-sm font-semibold text-slate-700">Capacidad y equipamiento</div>
                <p className="text-xs text-slate-500">Capacidad de personas que entran en la unidad. Las cocheras deben tener capacidad 0.</p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-600">Capacidad (personas)</Label>
                    <Input
                      data-testid="input-capacidad-personas"
                      type="number"
                      min="0"
                      value={unidadForm.capacidad_personas ?? ''}
                      onChange={(e) => setUnidadForm({ ...unidadForm, capacidad_personas: e.target.value })}
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-600">Escritorios</Label>
                    <Input
                      data-testid="input-escritorios-cantidad"
                      type="number"
                      min="0"
                      value={unidadForm.escritorios_cantidad ?? ''}
                      onChange={(e) => setUnidadForm({ ...unidadForm, escritorios_cantidad: e.target.value })}
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-600">Sillas</Label>
                    <Input
                      data-testid="input-sillas-cantidad"
                      type="number"
                      min="0"
                      value={unidadForm.sillas_cantidad ?? ''}
                      onChange={(e) => setUnidadForm({ ...unidadForm, sillas_cantidad: e.target.value })}
                      placeholder="0"
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Descripción de equipamiento</Label>
                  <Input
                    data-testid="input-descripcion-equipamiento"
                    value={unidadForm.descripcion_equipamiento || ''}
                    onChange={(e) => setUnidadForm({ ...unidadForm, descripcion_equipamiento: e.target.value })}
                    placeholder="Equipamiento, observaciones…"
                  />
                </div>
              </div>
            </TabsContent>

            {/* INVERSIÓN */}
            <TabsContent value="inversion" className="space-y-4 mt-4">
              <div className="rounded-md border border-slate-200 p-3 space-y-3">
                <div className="text-sm font-semibold text-slate-700">Inversión / Compra</div>
                <p className="text-[11px] text-slate-500">Estos datos NO se calculan automáticamente. El IVA queda en blanco si no lo completás.</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Fecha de adquisición</Label>
                    <Input
                      type="date"
                      data-testid="input-fecha-adquisicion"
                      value={unidadForm.fecha_adquisicion || ''}
                      onChange={(e) => setUnidadForm({ ...unidadForm, fecha_adquisicion: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Moneda</Label>
                    <Select
                      value={monto.moneda || 'USD'}
                      onValueChange={(v) => setMontoField('moneda', v)}
                    >
                      <SelectTrigger data-testid="select-moneda"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="ARS">ARS</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Monto Neto</Label>
                    <Input
                      type="number"
                      step="0.01"
                      data-testid="input-monto-neto"
                      value={monto.monto ?? ''}
                      onChange={(e) => setMontoField('monto', e.target.value === '' ? null : parseFloat(e.target.value))}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">IVA</Label>
                    <Input
                      type="number"
                      step="0.01"
                      data-testid="input-iva"
                      value={monto.iva ?? ''}
                      onChange={(e) => setMontoField('iva', e.target.value === '' ? null : parseFloat(e.target.value))}
                      placeholder="(opcional)"
                    />
                  </div>
                </div>
                <div className="pt-2 border-t border-slate-100">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Total compra IVA incluido</Label>
                    <div className="px-3 py-2 rounded-md bg-slate-50 text-sm text-slate-700" data-testid="total-compra-readonly">
                      {(() => {
                        const props = selectedUnidad?.propietarios || [];
                        const sumMtc = props.reduce((acc, p) => acc + (p?.monto_total_compra?.monto || 0), 0);
                        const mon = props.find(p => p?.monto_total_compra?.moneda)?.monto_total_compra?.moneda
                          || monto.moneda || 'USD';
                        // Si hay monto_total_compra cargado en propietarios, usar esa suma.
                        if (sumMtc > 0) {
                          return `${mon} ${sumMtc.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                        }
                        // Fallback: monto_inversion.monto + iva
                        const tieneMonto = monto.monto != null && monto.monto !== '';
                        const tieneIva = monto.iva != null && monto.iva !== '';
                        if (!tieneMonto && !tieneIva) return '—';
                        if (tieneMonto && tieneIva) {
                          const tot = Number(monto.monto) + Number(monto.iva);
                          return `${mon} ${tot.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                        }
                        const v = tieneMonto ? Number(monto.monto) : Number(monto.iva);
                        const label = tieneMonto ? 'Neto' : 'IVA';
                        const faltante = tieneMonto ? 'IVA sin completar' : 'Monto Neto sin completar';
                        return (
                          <span>
                            <span>{mon} {v.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            <span className="text-[10px] text-amber-600 ml-2">({label} solo · {faltante})</span>
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* DOCUMENTACIÓN */}
            <TabsContent value="documentacion" className="space-y-4 mt-4">
              {!selectedUnidad ? (
                <p className="text-sm text-slate-500">Primero guardá la unidad para gestionar documentación.</p>
              ) : (
                <div className="space-y-3">
                  <div className="rounded-md border border-slate-200 p-3 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold text-slate-700">Carta oferta</div>
                      <div className="text-xs mt-0.5">
                        {tieneCartaOferta
                          ? <span className="text-emerald-700">Cargada · {selectedUnidad.carta_oferta_archivo?.filename || 'documento'}</span>
                          : <span className="text-red-600">Falta cargar</span>}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => onOpenCartaOferta && onOpenCartaOferta(selectedUnidad)}
                      data-testid="doc-carta-oferta-btn"
                    >
                      {tieneCartaOferta ? <FileText className="w-3.5 h-3.5 mr-1" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                      {tieneCartaOferta ? 'Ver / Reemplazar' : 'Cargar'}
                    </Button>
                  </div>

                  {isTorreEmilia ? (
                    /* Torre Emilia: escritura general del edificio, sólo lectura */
                    <div className="rounded-md border border-slate-200 p-3 flex items-center justify-between" data-testid="doc-escritura-general-row">
                      <div>
                        <div className="text-sm font-semibold text-slate-700">Escritura general</div>
                        <div className="text-xs mt-0.5">
                          {escrituraGeneralEdificio?.filename
                            ? <span className="text-emerald-700">Cargada · {escrituraGeneralEdificio.filename}</span>
                            : (
                              <span>
                                <span className="text-red-600">Falta cargar</span>
                                <span className="text-slate-500"> · Se carga desde la configuración del edificio</span>
                              </span>
                            )}
                        </div>
                      </div>
                      {escrituraGeneralEdificio?.filename && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => onDownloadEscrituraGeneral && onDownloadEscrituraGeneral()}
                          data-testid="doc-escritura-general-btn"
                        >
                          <FileText className="w-3.5 h-3.5 mr-1" />
                          Ver / Descargar
                        </Button>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="rounded-md border border-slate-200 p-3 flex items-center justify-between">
                        <div>
                          <div className="text-sm font-semibold text-slate-700">Posesión</div>
                          <div className="text-xs mt-0.5">
                            {tienePosesion
                              ? <span className="text-emerald-700">Cargada · {selectedUnidad.posesion_archivo?.filename || 'documento'}</span>
                              : <span className="text-red-600">Falta cargar</span>}
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => onOpenPosesion && onOpenPosesion(selectedUnidad)}
                          data-testid="doc-posesion-btn"
                        >
                          {tienePosesion ? <FileText className="w-3.5 h-3.5 mr-1" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                          {tienePosesion ? 'Ver / Reemplazar' : 'Cargar'}
                        </Button>
                      </div>
                      <div className="rounded-md border border-slate-200 p-3 flex items-center justify-between">
                        <div>
                          <div className="text-sm font-semibold text-slate-700">Escritura</div>
                          <div className="text-xs mt-0.5">
                            {tieneEscritura
                              ? <span className="text-emerald-700">Cargada · {selectedUnidad.escritura_archivo?.filename || 'documento'}</span>
                              : <span className="text-red-600">Falta cargar</span>}
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => onOpenEscritura && onOpenEscritura(selectedUnidad)}
                          data-testid="doc-escritura-btn"
                        >
                          {tieneEscritura ? <FileText className="w-3.5 h-3.5 mr-1" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                          {tieneEscritura ? 'Ver / Reemplazar' : 'Cargar'}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </TabsContent>

            {/* SERVICIOS / IMPUESTOS */}
            <TabsContent value="servicios" className="space-y-4 mt-4">
              <div className="rounded-md border border-slate-200 p-3 space-y-3">
                <div className="text-sm font-semibold text-slate-700">Servicios e impuestos</div>
                <p className="text-[11px] text-slate-500">Marcá "No aplica" si la unidad no tiene ese servicio. Eso cuenta como dato cargado.</p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'nic_energia_electrica', label: 'NIC Energía', flag: 'nic_energia_no_aplica' },
                    { key: 'nic_agua', label: 'NIC Agua', flag: 'nic_agua_no_aplica' },
                    { key: 'tasa_municipal', label: 'Tasa Municipal', flag: 'tasa_municipal_no_aplica' },
                    { key: 'tasa_comercial', label: 'Tasa Comercial', flag: 'tasa_comercial_no_aplica' },
                  ].map(it => (
                    <div key={it.key} className="space-y-1">
                      <Label className="text-xs">{it.label}</Label>
                      <Input
                        data-testid={`input-${it.key}`}
                        value={unidadForm[it.key] || ''}
                        onChange={(e) => setUnidadForm({ ...unidadForm, [it.key]: e.target.value })}
                        placeholder="Número"
                        disabled={!!unidadForm[it.flag]}
                        className={unidadForm[it.flag] ? 'bg-slate-100' : ''}
                      />
                      <label className="flex items-center gap-2 text-[11px] text-slate-500 cursor-pointer">
                        <input
                          type="checkbox"
                          data-testid={`check-${it.flag}`}
                          checked={!!unidadForm[it.flag]}
                          onChange={(e) => setUnidadForm({ ...unidadForm, [it.flag]: e.target.checked, [it.key]: e.target.checked ? '' : unidadForm[it.key] })}
                          className="rounded border-slate-300"
                        />
                        No aplica
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="flex items-center justify-between gap-2">
            <div>
              {selectedUnidad && onDeleteUnidad && (
                <Button
                  type="button"
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => onDeleteUnidad(selectedUnidad)}
                  data-testid="delete-unidad-from-modal"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar unidad
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setIsUnidadDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid="save-unidad-btn">{saving ? 'Guardando...' : selectedUnidad ? 'Actualizar' : 'Crear'}</Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
